import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useT } from "@/i18n";

// §5.5 — "Which best describes your current role?" fixed option set.
// Shared by every diagnostic so the role taxonomy can never drift apart.
// Labels are localized via diagnosticUi.role_* i18n keys (value stays the
// stable English-ish slug stored in the run regardless of UI language).
const ROLE_VALUES = [
  "founder_owner", "c_level_executive", "director_head_of", "manager_team_lead",
  "senior_professional_specialist", "professional_individual_contributor", "student", "other",
];

/**
 * Right-panel contact form — shared by every diagnostic. Same
 * fields/order/validation throughout (first name + email + role
 * [+ other-role text] required, company optional, marketing
 * consent its own unchecked-by-default checkbox). Heading/
 * subheading/consent copy are passed in per diagnostic since each
 * mentions a different report (e.g. "and recommended exercise").
 */
export const ContactPanel = ({
  onSubmit, submitting, error,
  headingText = "Almost there",
  subheadingText = "Where should we send your free personalised report?",
  consentText = "I'd also like to receive practical communication guidance and InterAct updates by email.",
  // InterAct Tracking Foundation milestone (2026-10-09 correction) —
  // optional, backward-compatible: a diagnostic page can pass this to
  // fire its own `form_started` on the FIRST real field mutation here
  // (tracking_registry.json: "First field mutation without value
  // capture"), not merely because this panel rendered. Defaults to a
  // no-op so existing callers (Next Presentation Readiness Check)
  // that don't pass it keep their current behavior unchanged.
  onFirstFieldMutation = () => {},
}) => {
  const t = useT();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [role, setRole] = useState("");
  const [otherRoleText, setOtherRoleText] = useState("");
  const [company, setCompany] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const firstMutationFiredRef = useRef(false);
  const markFirstMutation = () => {
    if (firstMutationFiredRef.current) return;
    firstMutationFiredRef.current = true;
    onFirstFieldMutation();
  };

  const roleNeedsOtherText = role === "other";
  const canSubmit = email.trim() && firstName.trim() && role && (!roleNeedsOtherText || otherRoleText.trim());

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      email: email.trim(),
      firstName: firstName.trim(),
      role,
      otherRoleText: roleNeedsOtherText ? otherRoleText.trim() : "",
      company: company.trim(),
      marketingConsent,
    });
  };

  return (
    <div data-testid="readiness-contact-card">
      <h2 className="font-serif text-2xl md:text-3xl text-charcoal mb-2">{headingText}</h2>
      <p className="font-body text-sm text-ink-soft mb-6">{subheadingText}</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          data-testid="readiness-contact-name"
          required
          placeholder={t("diagnosticUi.first_name_placeholder")}
          value={firstName}
          onChange={(e) => { markFirstMutation(); setFirstName(e.target.value); }}
          className="bg-paper-deep border-ink/15 text-ink h-12"
        />
        <Input
          data-testid="readiness-contact-email"
          type="email"
          required
          placeholder={t("diagnosticUi.email_placeholder")}
          value={email}
          onChange={(e) => { markFirstMutation(); setEmail(e.target.value); }}
          className="bg-paper-deep border-ink/15 text-ink h-12"
        />
        <Select value={role} onValueChange={(value) => { markFirstMutation(); setRole(value); }}>
          <SelectTrigger data-testid="readiness-contact-role" className="bg-paper-deep border-ink/15 text-ink h-12">
            <SelectValue placeholder={t("diagnosticUi.role_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {ROLE_VALUES.map((value) => (
              <SelectItem key={value} value={value} data-testid={`readiness-contact-role-${value}`}>
                {t(`diagnosticUi.role_${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {roleNeedsOtherText && (
          <Input
            data-testid="readiness-contact-role-other-text"
            required
            placeholder={t("diagnosticUi.role_other_text_placeholder")}
            value={otherRoleText}
            onChange={(e) => { markFirstMutation(); setOtherRoleText(e.target.value); }}
            className="bg-paper-deep border-ink/15 text-ink h-12"
          />
        )}
        <Input
          data-testid="readiness-contact-company"
          placeholder={t("diagnosticUi.company_placeholder")}
          value={company}
          onChange={(e) => { markFirstMutation(); setCompany(e.target.value); }}
          className="bg-paper-deep border-ink/15 text-ink h-12"
        />
        <label className="flex items-start gap-2.5 cursor-pointer text-ink">
          <input
            type="checkbox"
            data-testid="readiness-marketing-consent-checkbox"
            checked={marketingConsent}
            onChange={(e) => { markFirstMutation(); setMarketingConsent(e.target.checked); }}
            className="mt-1"
          />
          <span className="font-body text-xs leading-relaxed text-ink-soft">{consentText}</span>
        </label>

        {error && (
          <p data-testid="readiness-contact-error" className="font-body text-sm text-terracotta-dark">
            {error}
          </p>
        )}

        <Button
          type="submit"
          data-testid="readiness-contact-submit"
          disabled={submitting || !canSubmit}
          className="w-full py-6 text-base font-medium rounded-full bg-terracotta text-paper hover:bg-terracotta-dark transition-colors"
        >
          {submitting ? t("diagnosticUi.submitting_label") : t("diagnosticUi.submit_label")}
        </Button>
      </form>
    </div>
  );
};

export default ContactPanel;
